import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { formatDate } from './membership-format';
import type { MembershipCard } from '../../types/membership';

interface MembershipSummaryProps {
  cards: MembershipCard[];
  /**
   * The furthest expiry among the cards this gym counts — its own plus the
   * national ones it has accepted. Worked out by the API.
   */
  expiresOn: string | null;
  /** National cards from other gyms still waiting on a decision here. */
  pendingCount: number;
}

/**
 * How long a roster row is covered for, at a glance: the latest date any of the
 * member's cards runs to, opening onto the body and date of each.
 *
 * Only the total is worth a line on a list of two hundred people; the breakdown
 * is what somebody checks when the total looks wrong, so it stays one click
 * away rather than on screen.
 */
export default function MembershipSummary({
  cards,
  expiresOn,
  pendingCount,
}: MembershipSummaryProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  if (!expiresOn && cards.length === 0) {
    return (
      <p className="mt-2 text-sm text-muted-foreground">
        {t('gymAdmin.members.membership.noneOnRoster')}
      </p>
    );
  }

  const expired = expiresOn !== null && expiresOn < new Date().toISOString().slice(0, 10);
  const Chevron = open ? ChevronDown : ChevronRight;

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={() => setOpen((wasOpen) => !wasOpen)}
        aria-expanded={open}
        className={cn(
          'flex items-center gap-1 rounded text-sm font-medium hover:underline',
          expired ? 'text-destructive' : 'text-foreground'
        )}
      >
        <Chevron className="size-4" />
        {expired && <AlertTriangle className="size-3" />}
        {t('gymAdmin.members.membership.expiryLabel', {
          date: expiresOn ? formatDate(expiresOn) : t('gymAdmin.members.membership.noExpiry'),
        })}
      </button>

      {/* A national card from another gym does not count until somebody here
          says so, which is a thing to do rather than a thing to know. */}
      {pendingCount > 0 && (
        <p className="mt-1 text-sm text-muted-foreground">
          {t('gymAdmin.members.membership.pendingCount', { count: pendingCount })}
        </p>
      )}

      {open && (
        <ul className="mt-2 space-y-1 border-l pl-3 text-sm">
          {cards.map((card) => (
            <li
              key={card.id}
              className={cn(
                'flex flex-wrap items-baseline gap-x-2',
                !card.counts_toward_expiry && 'text-muted-foreground'
              )}
            >
              <span className="font-medium">{card.organization?.name}</span>
              <span className="text-muted-foreground">{card.number}</span>
              {card.acceptance_status === 'pending' && (
                <span>({t('gymAdmin.members.membership.pending')})</span>
              )}
              {card.acceptance_status === 'rejected' && (
                <span>({t('gymAdmin.members.membership.rejected')})</span>
              )}
              <span className={cn('ml-auto', card.expired && 'text-destructive')}>
                {card.expires_on
                  ? formatDate(card.expires_on)
                  : t('gymAdmin.members.membership.noExpiry')}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
